import { FC } from 'react'
import React from 'react'

import { Button, Card, Grid, Tag } from '@factorialco/deprecated-design-system'
import { motion } from 'framer-motion'
import { AvailableModules } from 'generated/featureDefinitions'
import { CategorySectionFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import RequestUpgradeModal from 'modules/upsell/components/RequestUpgradeModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

interface Props {
  templates: CategorySectionFragment[]
  trainingsPolicies: {
    checkTrainingsModule: boolean
    checkTrainingsPermission: boolean
  }
}

export const CategoriesSection: FC<Props> = ({ trainingsPolicies, templates }) => {
  const categoriesCardValues = [
    {
      category: 'onboarding',
      name: i18n.t('processes.home_screen.onboarding.title'),
      description: i18n.t('processes.home_screen.onboarding.description'),
    },
    {
      category: 'offboarding',
      name: i18n.t('processes.home_screen.offboarding.title'),
      description: i18n.t('processes.home_screen.offboarding.description'),
    },
    {
      category: 'training',
      name: i18n.t('processes.home_screen.training.title'),
      description: i18n.t('processes.home_screen.training.description'),
    },
    {
      category: 'custom',
      name: i18n.t('processes.home_screen.custom.title'),
      description: i18n.t('processes.home_screen.custom.description'),
    },
  ]

  const isNew = (category: string) =>
    templates.find((template) => template.category === category)?.new

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      transition={{ staggerChildren: 0.07, delayChildren: 0.1 }}
    >
      <Grid component="nav" variant="medium">
        {categoriesCardValues
          .map(({ category, name, description }) => {
            if (category === 'training' && !trainingsPolicies.checkTrainingsModule) {
              return <TrainingUpsell key={category} name={name} description={description} />
            } else if (category === 'training' && !trainingsPolicies.checkTrainingsPermission) {
              return null
            } else {
              return (
                <Item
                  key={category}
                  category={category}
                  name={name}
                  description={description}
                  isNew={isNew(category)}
                />
              )
            }
          })
          .filter(Boolean)}
      </Grid>
    </motion.div>
  )
}

interface ItemProps {
  category: string
  name: string
  description: string
  isNew?: boolean
}

const Item = ({ category, name, description, isNew }: ItemProps) => {
  const { navigation } = useNavigation()

  const status = isNew
    ? {
        label: i18n.t('marketplace.modules.new'),
        color: 'lime',
      }
    : undefined

  const toUrl = navigation.processes.template.fullPath({
    category: category,
  })

  const toLabel = i18n.t('processes.process_from_template.go_to', { name: name })

  return (
    <Card to={toUrl}>
      {status && (
        <Card.Eyebrow>
          <Tag type="lime">{status.label}</Tag>
        </Card.Eyebrow>
      )}
      <Card.Title>{name}</Card.Title>
      <Card.Description>{description}</Card.Description>
      <Card.Link to={toUrl}>{toLabel}</Card.Link>
    </Card>
  )
}

interface TrainingUpsellProps {
  name: string
  description: string
}

const TrainingUpsell = ({ name, description }: TrainingUpsellProps) => {
  const [modalOpen, setModalOpen] = React.useState(false)
  return (
    <>
      <Card>
        <Card.Eyebrow>
          <Tag leftItem="icon" type="grey" icon="lock-locked" iconColor="tangerine1000">
            {i18n.t('processes.home_screen.training.upsell.tag')}
          </Tag>
        </Card.Eyebrow>
        <Card.Title>{name}</Card.Title>
        <Card.Description>{description}</Card.Description>
        <Card.Content>
          <Button
            type="upgrade"
            onClick={() => {
              setModalOpen(true)
            }}
          >
            {i18n.t('processes.home_screen.training.upsell.button')}
          </Button>
        </Card.Content>
      </Card>
      {modalOpen && (
        <RequestUpgradeModal
          moduleRequested={AvailableModules.trainings}
          requestReason={ValidReasons.MissingAddon}
          requestSource="workflows_home"
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  )
}
